/**
 * Multi-City Configuration
 * Data sources and configuration for each supported city
 */

export const CITIES = {
  nyc: {
    id: 'nyc',
    name: 'New York City',
    shortName: 'NYC',
    state: 'NY',
    subdomain: 'nyc',
    status: 'live', // live, coming_soon, beta
    heroImage: '/images/nyc-skyline.jpg',
    color: '#3b82f6', // Blue
    tagline: 'NYC Building Compliance Platform',
    description: 'Access 41M+ compliance records across DOB, FDNY, ECB, HPD, OATH and permit data for all five boroughs.',
    stats: {
      properties: '1.1M+',
      violations: '41M+',
      agencies: 12
    },
    dataSources: [
      { id: 'dob_violations', name: 'DOB Violations', icon: '🏗️', color: '#ef4444', agency: 'Dept of Buildings', records: '2.5M' },
      { id: 'ecb_violations', name: 'ECB Violations', icon: '⚖️', color: '#8b5cf6', agency: 'Environmental Control Board', records: '1.8M' },
      { id: 'hpd_violations', name: 'HPD Violations', icon: '🏠', color: '#06b6d4', agency: 'Housing Preservation', records: '10.7M' },
      { id: 'oath_hearings', name: 'OATH Hearings', icon: '⚖️', color: '#f59e0b', agency: 'Office of Admin Trials', records: '21.4M' },
      { id: 'permits', name: 'DOB BIS Applications', icon: '📋', color: '#10b981', agency: 'Dept of Buildings', records: '2.7M' },
      { id: 'dob_now', name: 'DOB NOW Build', icon: '📁', color: '#3b82f6', agency: 'Dept of Buildings', records: '856K' },
      { id: 'fire_violations', name: 'FDNY Violations', icon: '🔥', color: '#f97316', agency: 'Fire Department', records: '—' },
      { id: 'dot_violations', name: 'DOT Violations', icon: '🚧', color: '#eab308', agency: 'Dept of Transportation', records: '311K' },
      { id: 'dep_environmental', name: 'DEP Environmental', icon: '🌿', color: '#22c55e', agency: 'Dept of Environmental Protection', records: '1M' },
      { id: 'certificate_of_occupancy', name: 'Certificates of Occupancy', icon: '🏢', color: '#ec4899', agency: 'Dept of Buildings', records: '143K' },
      { id: 'oer_sites', name: 'OER Cleanup Sites', icon: '☢️', color: '#a855f7', agency: 'Office of Enviro Remediation', records: '—' },
      { id: 'e_designations', name: 'E-Designations', icon: '🗺️', color: '#64748b', agency: 'City Planning', records: '—' }
    ],
    boroughs: ['Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island']
  },
  
  chicago: {
    id: 'chicago',
    name: 'Chicago',
    shortName: 'CHI',
    state: 'IL',
    subdomain: 'chicago',
    status: 'coming_soon',
    heroImage: '/images/chicago-skyline.jpg',
    color: '#06b6d4', // Cyan
    tagline: 'Chicago Building Compliance Platform',
    description: 'Track building violations, permits, and inspections across Cook County.',
    stats: {
      properties: '680K+',
      violations: '1.2M+',
      agencies: 5
    },
    dataSources: [
      { id: 'building_violations', name: 'Building Violations', icon: '🏗️', color: '#ef4444', agency: 'Dept of Buildings' },
      { id: 'fire_violations', name: 'Fire Violations', icon: '🔥', color: '#f97316', agency: 'Chicago Fire Dept' },
      { id: 'business_licenses', name: 'Business Licenses', icon: '📋', color: '#10b981', agency: 'Business Affairs' },
      { id: 'food_inspections', name: 'Food Inspections', icon: '🍴', color: '#8b5cf6', agency: 'Public Health' },
      { id: 'building_permits', name: 'Building Permits', icon: '📁', color: '#3b82f6', agency: 'Dept of Buildings' }
    ],
    neighborhoods: ['Loop', 'Lincoln Park', 'Wicker Park', 'Hyde Park', 'Lakeview', 'River North']
  },
  
  la: {
    id: 'la',
    name: 'Los Angeles',
    shortName: 'LA',
    state: 'CA',
    subdomain: 'la',
    status: 'coming_soon',
    heroImage: '/images/la-skyline.jpg',
    color: '#f97316', // Orange
    tagline: 'Los Angeles Building Compliance Platform',
    description: 'Monitor code enforcement, permits, and inspections across LA County.',
    stats: {
      properties: '2.4M+',
      violations: '890K+',
      agencies: 6
    },
    dataSources: [
      { id: 'code_enforcement', name: 'Code Enforcement', icon: '🏗️', color: '#ef4444', agency: 'LADBS' },
      { id: 'fire_violations', name: 'Fire Violations', icon: '🔥', color: '#f97316', agency: 'LAFD' },
      { id: 'building_permits', name: 'Building Permits', icon: '📋', color: '#10b981', agency: 'LADBS' },
      { id: 'housing_violations', name: 'Housing Violations', icon: '🏠', color: '#06b6d4', agency: 'LAHD' },
      { id: 'planning_cases', name: 'Planning Cases', icon: '📁', color: '#3b82f6', agency: 'City Planning' },
      { id: 'zoning_admin', name: 'Zoning Administration', icon: '🗺️', color: '#8b5cf6', agency: 'City Planning' }
    ],
    neighborhoods: ['Downtown', 'Hollywood', 'Santa Monica', 'Venice', 'Westwood', 'Silver Lake']
  },
  
  sf: {
    id: 'sf',
    name: 'San Francisco',
    shortName: 'SF',
    state: 'CA',
    subdomain: 'sf',
    status: 'coming_soon',
    heroImage: '/images/sf-skyline.jpg',
    color: '#10b981', // Emerald
    tagline: 'San Francisco Building Compliance Platform',
    description: 'Access DBI violations, fire inspections, and permit data for SF properties.',
    stats: {
      properties: '380K+',
      violations: '420K+',
      agencies: 5
    },
    dataSources: [
      { id: 'building_violations', name: 'Building Violations', icon: '🏗️', color: '#ef4444', agency: 'Dept of Building Inspection' },
      { id: 'fire_violations', name: 'Fire Violations', icon: '🔥', color: '#f97316', agency: 'SF Fire Dept' },
      { id: 'housing_violations', name: 'Housing Violations', icon: '🏠', color: '#06b6d4', agency: 'Housing Inspection' },
      { id: 'building_permits', name: 'Building Permits', icon: '📋', color: '#10b981', agency: 'DBI' },
      { id: 'planning_cases', name: 'Planning Cases', icon: '📁', color: '#3b82f6', agency: 'SF Planning' }
    ],
    neighborhoods: ['SOMA', 'Mission', 'Marina', 'Castro', 'Noe Valley', 'Financial District']
  },
  
  boston: {
    id: 'boston',
    name: 'Boston',
    shortName: 'BOS',
    state: 'MA',
    subdomain: 'boston',
    status: 'coming_soon',
    heroImage: '/images/boston-skyline.jpg',
    color: '#8b5cf6', // Purple
    tagline: 'Boston Building Compliance Platform',
    description: 'Track ISD violations, permits, and inspections across Greater Boston.',
    stats: {
      properties: '180K+',
      violations: '310K+',
      agencies: 4
    },
    dataSources: [
      { id: 'code_violations', name: 'Code Violations', icon: '🏗️', color: '#ef4444', agency: 'ISD' },
      { id: 'fire_violations', name: 'Fire Violations', icon: '🔥', color: '#f97316', agency: 'Boston Fire' },
      { id: 'building_permits', name: 'Building Permits', icon: '📋', color: '#10b981', agency: 'ISD' },
      { id: 'housing_violations', name: 'Housing Violations', icon: '🏠', color: '#06b6d4', agency: 'ISD' }
    ],
    neighborhoods: ['Back Bay', 'Beacon Hill', 'South End', 'Seaport', 'Cambridge', 'Somerville']
  },
  
  dc: {
    id: 'dc',
    name: 'Washington DC',
    shortName: 'DC',
    state: 'DC',
    subdomain: 'dc',
    status: 'live',
    heroImage: '/images/dc-skyline.jpg',
    color: '#ec4899', // Pink
    tagline: 'Washington DC Building & Property Data Platform',
    description: 'Access building permits, property assessments, and environmental enforcement across the District.',
    stats: {
      properties: '165K+',
      violations: '180K+',
      agencies: 5
    },
    dataSources: [
      { id: 'dc_building_permits', name: 'Building Permits', icon: '📋', color: '#10b981', agency: 'DC DOB' },
      { id: 'dc_service_requests', name: '311 Service Requests', icon: '📞', color: '#ef4444', agency: 'DC 311' },
      { id: 'dc_affordable_housing', name: 'Affordable Housing', icon: '🏠', color: '#06b6d4', agency: 'DMPED' },
      { id: 'dc_cama_residential', name: 'Residential Properties', icon: '🏡', color: '#8b5cf6', agency: 'OCFO' },
      { id: 'dc_cama_commercial', name: 'Commercial Properties', icon: '🏢', color: '#f59e0b', agency: 'OCFO' }
    ],
    neighborhoods: ['Capitol Hill', 'Georgetown', 'Dupont Circle', 'Adams Morgan', 'Navy Yard', 'NoMa']
  }
};

export const CITY_ORDER = ['nyc', 'dc', 'chicago', 'la', 'sf', 'boston'];

export const getLiveCity = () => CITIES.nyc; // Primary live city
export const getLiveCities = () => CITY_ORDER.filter(id => CITIES[id].status === 'live').map(id => CITIES[id]);
export const getComingSoonCities = () => CITY_ORDER.filter(id => CITIES[id].status === 'coming_soon').map(id => CITIES[id]);
export const getAllCities = () => CITY_ORDER.map(id => CITIES[id]);
